/**
 * NeedsAttention.jsx
 * Posts that failed to publish, and what can be done about each.
 *
 * Grouped by post rather than by platform, so a post that failed on both is
 * one row with one Retry button. Retry re-publishes only the half that failed;
 * anything already live is untouched.
 *
 * Dependencies: services/api.js, components/Icons.jsx
 */

import { useState } from 'react';
import { Bell, Check, Refresh, Server, Warning } from '../Icons.jsx';
import { api } from '../../services/api.js';

export default function NeedsAttention({ failed = [], onChanged, navigate }) {
  const [retrying, setRetrying] = useState(null);
  const [discarding, setDiscarding] = useState(null);
  const [note, setNote] = useState(null);

  /** Re-publishes only the failed half; anything already live is untouched. */
  async function retry(post) {
    setRetrying(post.id);
    setNote(null);
    try {
      const res = await api.posts.retry(post.id);
      const failures = res.results.filter((r) => r.status === 'failed');
      setNote(
        failures.length
          ? `Still failing: ${failures.map((f) => `${f.platform} — ${f.error}`).join('; ')}`
          : 'Published.',
      );
      await onChanged();
    } catch (e) {
      setNote(e.message);
    } finally {
      setRetrying(null);
    }
  }

  /** Deletes the failed half. It never went out, so nothing on the platform changes. */
  async function discard(post) {
    const where = (post.platforms ?? '').split(',').join(' and ');
    const ok = window.confirm(
      `Delete the failed ${where} post for ${post.brand_name}?\n\n` +
        'It never went out, so nothing on Facebook or Instagram changes. ' +
        'Anything from this post that did go out is left as it is.',
    );
    if (!ok) return;

    setDiscarding(post.id);
    setNote(null);
    try {
      const res = await api.posts.discardFailed(post.id);
      setNote(res.message);
      await onChanged();
    } catch (e) {
      setNote(e.message);
    } finally {
      setDiscarding(null);
    }
  }

  return (
    <section className="card">
      <div className="card__head">
        <span className={`tile ${failed.length ? 'tile--danger' : 'tile--ok'}`}>
          <Bell size={21} />
        </span>
        <div className="card__head-text">
          <h2>Needs attention</h2>
          <p>Anything that failed to publish shows up here.</p>
        </div>
      </div>

      {note && (
        <div className="notice notice--info">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {failed.length === 0 ? (
        <div className="row">
          <span className="tile tile--sm tile--ok">
            <Check size={17} />
          </span>
          <div>
            <div className="row__label">All good</div>
            <div className="row__sub">
              Nothing needs your attention right now. We’ll flag anything that fails to publish.
            </div>
          </div>
        </div>
      ) : (
        <div className="rows">
          {failed.map((f) => (
            <div className="row row--wrap" key={f.id}>
              <span className="tile tile--sm tile--danger">
                <Warning size={17} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="row__label">
                  {f.brand_name} — {(f.platforms ?? '').split(',').join(' and ')} failed
                </div>
                <div className="row__sub">{f.last_error || 'No error recorded'}</div>
              </div>
              <div className="row__end">
                <button
                  className="btn btn--sm btn--primary"
                  onClick={() => retry(f)}
                  disabled={retrying === f.id}
                >
                  <Refresh size={15} />
                  {retrying === f.id ? 'Retrying…' : 'Retry'}
                </button>
                <button className="btn btn--sm" onClick={() => navigate(`/create?post=${f.id}`)}>
                  Edit
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  onClick={() => discard(f)}
                  disabled={discarding === f.id}
                  title="Delete the failed post. It never went out, so no platform changes."
                >
                  {discarding === f.id ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ============================================
// THE PLUMBING
// ============================================

/**
 * API, database and sign-in, as one line each.
 *
 * It used to be the largest card on the screen, which put the least
 * interesting thing about a working system at the top of it. It is now a strip
 * that stays quiet while everything is fine and is impossible to miss when it
 * is not.
 */
export function SystemStatus({ apiUp, dbOk, user, brands, connected, checking, onRefresh }) {
  const allGood = apiUp && dbOk;

  return (
    <div className={`sysbar${allGood ? '' : ' sysbar--bad'}`}>
      <span className="sysbar__icon">
        <Server size={16} />
      </span>

      <span className={`badge ${apiUp ? 'badge--ok' : 'badge--danger'}`}>
        <span className="dot" /> API {apiUp ? 'healthy' : 'down'}
      </span>
      <span className={`badge ${dbOk ? 'badge--ok' : 'badge--danger'}`}>
        <span className="dot" /> Database {dbOk ? 'OK' : 'unavailable'}
      </span>

      <span className="sysbar__text">
        {brands === 0 ? 'No brands yet' : `${connected} of ${brands} brands connected`}
      </span>
      <span className="sysbar__text sysbar__text--dim">{user || '—'}</span>

      <button className="btn btn--sm" onClick={onRefresh} disabled={checking}>
        <Refresh size={14} /> {checking ? 'Refreshing…' : 'Refresh'}
      </button>
    </div>
  );
}
